import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  Camera,
  CheckCircle,
  ClipboardList,
  Loader2,
  Pencil,
  Play,
  Printer,
  RotateCcw,
  Scale,
  Trash2,
} from 'lucide-react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PageHeader } from '@/components/efs/PageHeader';
import { KgValue } from '@/components/efs/KgValue';
import { formatDateTime } from '@/lib/format';
import { ApiClientError } from '@/lib/api-client';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';
import { LocalizedApiError, localizeApiError } from '@/features/audit-issues';
import { TruckPhotoConflictNotice } from './TruckPhotoConflictNotice';
import {
  evidenceDownloadUrl,
  EVIDENCE_MEDIA_ACCEPT,
  uploadEvidence,
} from '@/features/evidence/api';
import {
  EVIDENCE_MEDIA_MIME_GROUPS,
  FileLibraryPickerDialog,
  fileMatchesEvidenceMediaAccept,
} from '@/features/file-library';
import { useFileLibraryFeatureEnabled } from '@/features/file-library/useFileLibraryFeature';
import type { FileLibrarySelection, FileSourceMode } from '@/features/file-library/types';
import { useSuppliersList } from '@/features/masters/suppliers/useSuppliers';
import { usePackageTypesList } from '@/features/masters/package-types/usePackageTypes';
import { useWarehousesList } from '@/features/masters/warehouses/useWarehouses';

import {
  useInboundLotWithPackages,
  useConfirmInboundLot,
  useBulkAssignWarehouse,
  useDeleteInboundPackage,
  useLinkTruckPhoto,
  useRemoveTruckPhoto,
  useSetTruckPhotoKind,
  useBatchFacilityId,
} from './useInbound';
import type {
  InboundLotWithPackagesDetailDto,
  InboundPackageDto,
  PackageRevisionDto,
  RequiredTruckPhotoKind,
  TruckPhotoKind,
} from './types';
import { requiredTruckPhotoKindValues, truckPhotoKindValues } from './types';
import { InboundPackageEditDialog } from './InboundPackageEditDialog';
import { InboundPackageWeightCorrectionDialog } from './InboundPackageWeightCorrectionDialog';
import { WorksheetEditDialog } from './WorksheetEditDialog';
import { TruckPhotoDirectionZone } from './TruckPhotoDirectionZone';
import { attachInboundEvidenceFromLibrary } from './api';
import { DeleteInboundLotDialog } from './DeleteInboundLotDialog';

/** A `*` marker shown next to a field value that was modified in any
 *  past revision. Click any cell with this marker to open the row's
 *  revision history modal. */
const FieldEditedMark = ({ edited }: { edited: boolean }) =>
  edited ? <span className="ml-1 text-amber-900">*</span> : null;

const REQUIRED_TRUCK_KINDS = requiredTruckPhotoKindValues;

/** Build the set of field names that have ever changed for a package. */
const editedFieldSet = (revs: PackageRevisionDto[]): Set<string> => {
  const s = new Set<string>();
  for (const r of revs) {
    const fields = r.change_summary?.changed_fields ?? [];
    fields.forEach((f) => s.add(f));
  }
  return s;
};

interface RevisionsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pkg: InboundPackageDto | null;
  fieldLabel: (field: string) => string;
  valueLabel: (field: string, value: unknown) => string;
}

interface LotRevisionsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lotCode: string | undefined;
  revisions: PackageRevisionDto[];
  fieldLabel: (field: string) => string;
  valueLabel: (field: string, value: unknown) => string;
}

/** Modal listing every worksheet-level revision row for one lot. */
const LotRevisionsModal = ({
  open,
  onOpenChange,
  lotCode,
  revisions,
  fieldLabel,
  valueLabel,
}: LotRevisionsModalProps) => {
  const { t } = useTranslation(NS);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t('efs.operations.inbound.worksheet.revisions_title', {
              defaultValue: '進貨工作單 異動紀錄',
            })}
          </DialogTitle>
          {lotCode ? (
            <DialogDescription>
              <span className="font-mono text-xs">{lotCode}</span>
            </DialogDescription>
          ) : null}
        </DialogHeader>
        {revisions.length === 0 ? (
          <p className="text-sm text-efs-text-muted">
            {t('efs.operations.inbound.revisions.empty', { defaultValue: '異動紀錄なし' })}
          </p>
        ) : (
          <ol className="space-y-3">
            {revisions.map((r) => {
              const diff = r.change_summary?.diff ?? {};
              const fields = r.change_summary?.changed_fields ?? [];
              return (
                <li
                  key={r.revision_id}
                  className="rounded-md border border-efs-border bg-efs-bg-panel p-3 text-sm"
                >
                  <div className="flex items-baseline justify-between text-xs text-efs-text-muted">
                    <span>{new Date(r.changed_at).toLocaleString()}</span>
                    <span>{r.changed_by_name}</span>
                  </div>
                  <p className="mt-1 font-medium">{r.change_reason}</p>
                  {fields.length > 0 ? (
                    <ul className="mt-2 space-y-1 text-xs">
                      {fields.map((field) => {
                        const d = diff[field];
                        return (
                          <li key={field} className="font-mono">
                            <span className="text-amber-900">{fieldLabel(field)}</span>:{' '}
                            <span className="text-efs-text-muted">
                              {valueLabel(field, d?.before)}
                            </span>{' '}
                            →{' '}
                            <span className="text-emerald-900">{valueLabel(field, d?.after)}</span>
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </li>
              );
            })}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
};

/** Modal listing every revision row for one package — newest first.
 *  Shows actor, timestamp, reason, and a one-line summary of each
 *  changed field's before / after values. */
const RevisionsModal = ({
  open,
  onOpenChange,
  pkg,
  fieldLabel,
  valueLabel,
}: RevisionsModalProps) => {
  const { t } = useTranslation(NS);
  const revs = pkg?.revisions ?? [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t('efs.operations.inbound.revisions.title', {
              defaultValue: '異動紀錄',
            })}
          </DialogTitle>
          {pkg ? (
            <DialogDescription>
              <span className="font-mono text-xs">
                {pkg.package_code} — {pkg.raw_incoming_item_name}
              </span>
            </DialogDescription>
          ) : null}
        </DialogHeader>
        {revs.length === 0 ? (
          <p className="text-sm text-efs-text-muted">
            {t('efs.operations.inbound.revisions.empty', {
              defaultValue: '異動紀錄なし',
            })}
          </p>
        ) : (
          <ol className="space-y-3">
            {revs.map((r) => {
              const diff = r.change_summary?.diff ?? {};
              const fields = r.change_summary?.changed_fields ?? [];
              return (
                <li
                  key={r.revision_id}
                  className="rounded-md border border-efs-border bg-efs-bg-panel p-3 text-sm"
                >
                  <div className="flex items-baseline justify-between text-xs text-efs-text-muted">
                    <span>{new Date(r.changed_at).toLocaleString()}</span>
                    <span>{r.changed_by_name}</span>
                  </div>
                  <p className="mt-1 font-medium">{r.change_reason}</p>
                  {fields.length > 0 ? (
                    <ul className="mt-2 space-y-1 text-xs">
                      {fields.map((field) => {
                        const d = diff[field];
                        return (
                          <li key={field} className="font-mono">
                            <span className="text-amber-900">{fieldLabel(field)}</span>:{' '}
                            <span className="text-efs-text-muted">
                              {valueLabel(field, d?.before)}
                            </span>{' '}
                            →{' '}
                            <span className="text-emerald-900">{valueLabel(field, d?.after)}</span>
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </li>
              );
            })}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
};

const NS = 'operations';
const REQUIRED_TRUCK_PHOTO_COUNT = 3;
const PRINT_WORKSHEET_MIN_ROWS = 12;

// PackageStatusBadge removed — each row now shows a per-package completion
// percentage in its dedicated column.

const LotStatusBadge = ({ status }: { status: string }) => {
  const { t } = useTranslation(NS);
  const variantMap: Record<string, 'default' | 'secondary' | 'outline'> = {
    confirmed: 'default',
    imported: 'secondary',
    locked: 'outline',
  };
  return (
    <Badge variant={variantMap[status] ?? 'secondary'}>
      {t(`efs.operations.statuses.${status}`, { defaultValue: status })}
    </Badge>
  );
};

interface PrintInboundWorksheetProps {
  lot: InboundLotWithPackagesDetailDto;
  packages: InboundPackageDto[];
  supplierName: string;
  packageTypeOptions: string;
  warehouseOptions: string;
  packageTypeMap: Map<string, string>;
  warehouseMap: Map<string, string>;
}

const PrintInboundWorksheet = ({
  lot,
  packages,
  supplierName,
  packageTypeOptions,
  warehouseOptions,
  packageTypeMap,
  warehouseMap,
}: PrintInboundWorksheetProps) => {
  const { t } = useTranslation(NS);
  const blankRowCount = Math.max(PRINT_WORKSHEET_MIN_ROWS - packages.length, 3);
  const blankRows = Array.from({ length: blankRowCount }, (_, idx) => idx);

  return (
    <>
      <style>{`
        @media screen {
          .inbound-print-area {
            display: none;
          }
        }

        @media print {
          @page {
            size: A4 landscape;
            margin: 10mm;
          }

          body * {
            visibility: hidden !important;
          }

          .inbound-print-area,
          .inbound-print-area * {
            visibility: visible !important;
          }

          .inbound-print-area {
            display: block !important;
            position: absolute;
            inset: 0 auto auto 0;
            width: 100%;
            color: #000;
            background: #fff;
            font-family: Arial, "Noto Sans JP", sans-serif;
            font-size: 11px;
            line-height: 1.35;
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }

          .inbound-print-area h1 {
            margin: 0 0 6px;
            font-size: 18px;
          }

          .inbound-print-area table {
            width: 100%;
            border-collapse: collapse;
          }

          .inbound-print-area th,
          .inbound-print-area td {
            border: 1px solid #111;
            padding: 5px;
            vertical-align: top;
          }

          .inbound-print-area th {
            background: #f1f5f9;
            font-weight: 700;
          }

          .inbound-print-area .meta-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 6px;
            margin: 8px 0;
          }

          .inbound-print-area .meta-cell {
            border: 1px solid #111;
            padding: 5px;
            min-height: 34px;
          }

          .inbound-print-area .label {
            display: block;
            color: #334155;
            font-size: 9px;
            font-weight: 700;
          }

          .inbound-print-area .write-row td {
            height: 32px;
          }

          .inbound-print-area .note {
            margin: 6px 0;
            color: #111827;
            font-size: 10px;
          }
        }
      `}</style>
      <section className="inbound-print-area" aria-hidden="true">
        <h1>{t('efs.operations.inbound.print.title')}</h1>
        <div className="meta-grid">
          <div className="meta-cell">
            <span className="label">{t('efs.operations.inbound.fields.lot_code')}</span>
            {lot.lot_code}
          </div>
          <div className="meta-cell">
            <span className="label">{t('efs.operations.inbound.fields.inbound_date')}</span>
            {lot.inbound_date}
          </div>
          <div className="meta-cell">
            <span className="label">{t('efs.operations.inbound.fields.vehicle')}</span>
            {lot.vehicle ?? ''}
          </div>
          <div className="meta-cell">
            <span className="label">{t('efs.operations.inbound.fields.source_company')}</span>
            {supplierName}
          </div>
          <div className="meta-cell">
            <span className="label">{t('efs.operations.inbound.worksheet.measured_net_kg')}</span>
            {lot.measured_net_kg ? `${lot.measured_net_kg} kg` : ''}
          </div>
          <div className="meta-cell">
            <span className="label">{t('efs.operations.inbound.worksheet.arrival_at')}</span>
            {lot.arrival_at ? formatDateTime(lot.arrival_at) : ''}
          </div>
          <div className="meta-cell">
            <span className="label">{t('efs.operations.inbound.worksheet.departure_at')}</span>
            {lot.departure_at ? formatDateTime(lot.departure_at) : ''}
          </div>
          <div className="meta-cell">
            <span className="label">{t('efs.operations.inbound.print.package_type_options')}</span>
            {packageTypeOptions}
          </div>
          <div className="meta-cell">
            <span className="label">{t('efs.operations.inbound.print.warehouse_options')}</span>
            {warehouseOptions}
          </div>
          <div className="meta-cell">
            <span className="label">{t('efs.operations.inbound.print.media_check')}</span>
            {t('efs.operations.inbound.truck_photos.kind.left', { defaultValue: '左側' })} □{' '}
            {t('efs.operations.inbound.truck_photos.kind.right', { defaultValue: '右側' })} □{' '}
            {t('efs.operations.inbound.truck_photos.kind.rear', { defaultValue: '後方' })} □
          </div>
        </div>
        <p className="note">{t('efs.operations.inbound.print.scale_note')}</p>
        <table>
          <thead>
            <tr>
              <th style={{ width: '34px' }}>#</th>
              <th>{t('efs.operations.inbound.print.columns.item')}</th>
              <th>{t('efs.operations.inbound.print.columns.package_code')}</th>
              <th>{t('efs.operations.inbound.print.columns.gross_kg')}</th>
              <th>{t('efs.operations.inbound.print.columns.tare_kg')}</th>
              <th>{t('efs.operations.inbound.print.columns.net_kg')}</th>
              <th>{t('efs.operations.inbound.print.columns.package_type')}</th>
              <th>{t('efs.operations.inbound.print.columns.warehouse')}</th>
              <th>{t('efs.operations.inbound.print.columns.media')}</th>
              <th>{t('efs.operations.inbound.print.columns.notes')}</th>
            </tr>
          </thead>
          <tbody>
            {packages.map((pkg, idx) => (
              <tr key={pkg.inbound_package_id} className="write-row">
                <td>{idx + 1}</td>
                <td>{pkg.raw_incoming_item_name}</td>
                <td>{pkg.package_code}</td>
                <td>{pkg.gross_kg ?? ''}</td>
                <td>{pkg.tare_kg ?? ''}</td>
                <td>{pkg.net_kg ?? ''}</td>
                <td>
                  {pkg.package_type_id ? (packageTypeMap.get(pkg.package_type_id) ?? '') : ''}
                </td>
                <td>{pkg.warehouse_id ? (warehouseMap.get(pkg.warehouse_id) ?? '') : ''}</td>
                <td>□</td>
                <td>{pkg.notes ?? ''}</td>
              </tr>
            ))}
            {blankRows.map((idx) => (
              <tr key={`blank-${idx}`} className="write-row">
                <td>{packages.length + idx + 1}</td>
                <td />
                <td />
                <td />
                <td />
                <td />
                <td />
                <td />
                <td>□</td>
                <td />
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </>
  );
};

/**
 * Evidence thumbnail that plays video inline. The download endpoint serves
 * mp4/mov/webm with `Content-Type: video/*` and `Content-Disposition: inline`,
 * but the media kind cannot be told from the id alone. Images are the common
 * case, so render an <img> first and fall back to a <video> player only when the
 * bytes fail to decode as an image — i.e. the file is a video, not a photo.
 */
const EvidenceMedia = ({ fid, alt }: { fid: string; alt?: string }) => {
  const [asVideo, setAsVideo] = useState(false);
  const src = evidenceDownloadUrl(fid);
  if (asVideo) {
    return (
      <video
        src={src}
        controls
        playsInline
        preload="metadata"
        className="max-h-[60vh] w-full bg-black object-contain"
      />
    );
  }
  return (
    <img
      src={src}
      alt={alt ?? ''}
      onError={() => setAsVideo(true)}
      className="max-h-[60vh] w-full object-contain"
    />
  );
};

/**
 * Compact evidence thumbnail for grid cells: fills the cell with the actual
 * photo (object-cover) so operators see it without opening the lightbox. For
 * videos it shows the first frame with a play badge. Uses the same img→video
 * fallback as EvidenceMedia because the media kind is unknown from the id.
 */
const EvidenceThumb = ({ fid, alt }: { fid: string; alt?: string }) => {
  const [asVideo, setAsVideo] = useState(false);
  const src = evidenceDownloadUrl(fid);
  if (asVideo) {
    return (
      <>
        <video
          src={src}
          muted
          playsInline
          preload="metadata"
          className="h-full w-full object-cover"
        />
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <Play
            className="h-6 w-6 text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.85)]"
            fill="currentColor"
          />
        </span>
      </>
    );
  }
  return (
    <img
      src={src}
      alt={alt ?? ''}
      onError={() => setAsVideo(true)}
      className="h-full w-full object-cover"
    />
  );
};

export const InboundLotDetailPage = () => {
  const { t } = useTranslation([NS, 'issues']);
  const { batchId, lotId } = useParams();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const fileLibraryEnabled = useFileLibraryFeatureEnabled();
  const query = useInboundLotWithPackages(lotId);
  const confirm = useConfirmInboundLot();
  const setTruckKindMutation = useSetTruckPhotoKind();
  const linkTruckPhotoMutation = useLinkTruckPhoto();
  const removeTruckPhotoMutation = useRemoveTruckPhoto();

  const [editPkg, setEditPkg] = useState<InboundPackageDto | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [weightCorrectionPkg, setWeightCorrectionPkg] = useState<InboundPackageDto | null>(null);
  const [revPkg, setRevPkg] = useState<InboundPackageDto | null>(null);
  const [revOpen, setRevOpen] = useState(false);
  const [worksheetEditOpen, setWorksheetEditOpen] = useState(false);
  const [worksheetRevOpen, setWorksheetRevOpen] = useState(false);
  const [truckUploadingKinds, setTruckUploadingKinds] = useState<
    Partial<Record<TruckPhotoKind, boolean>>
  >({});
  const [truckSourceModes, setTruckSourceModes] = useState<Record<TruckPhotoKind, FileSourceMode>>({
    left: 'local',
    right: 'local',
    rear: 'local',
    other: 'local',
  });
  const [truckLibraryPickerKind, setTruckLibraryPickerKind] = useState<TruckPhotoKind | null>(null);
  const [truckUploadErrors, setTruckUploadErrors] = useState<
    Partial<Record<TruckPhotoKind, string>>
  >({});
  const [truckError, setTruckError] = useState<string | null>(null);
  const [truckKindUpdatingFid, setTruckKindUpdatingFid] = useState<string | null>(null);

  // Confirm-conflict details (spec-13 §8.7): stable codes identify the exact
  // affected photo card / missing directions so the page can highlight and
  // focus them instead of leaving the operator with a raw UUID sentence.
  const confirmConflict = useMemo(() => {
    const err = confirm.error;
    if (!(err instanceof ApiClientError)) return null;
    const d = (err.details ?? {}) as Record<string, unknown>;
    const evidenceIds = new Set<string>();
    if (typeof d.evidence_file_id === 'string') evidenceIds.add(d.evidence_file_id);
    if (Array.isArray(d.evidence_file_ids)) {
      for (const v of d.evidence_file_ids) if (typeof v === 'string') evidenceIds.add(v);
    }
    const missingDirections = Array.isArray(d.missing_directions)
      ? d.missing_directions.filter((v): v is string => typeof v === 'string')
      : [];
    return { code: err.code, evidenceIds, missingDirections };
  }, [confirm.error]);

  // Scroll AND focus the first invalid target after a failed confirmation
  // (spec-13 §8.7). For direction conflicts the focus target is the real
  // upload input of the missing direction's zone (fallback: the zone itself,
  // which carries tabIndex=-1).
  const truckCardRefs = useRef(new Map<string, HTMLButtonElement>());
  const truckZoneRefs = useRef(new Map<string, HTMLElement>());
  const truckZoneInputRefs = useRef(new Map<string, HTMLInputElement>());
  useEffect(() => {
    if (!confirmConflict) return;
    const firstCardId = [...confirmConflict.evidenceIds].find((id) =>
      truckCardRefs.current.has(id),
    );
    if (firstCardId) {
      const el = truckCardRefs.current.get(firstCardId);
      el?.scrollIntoView({ block: 'center' });
      el?.focus();
      return;
    }
    const firstDirection = confirmConflict.missingDirections.find(
      (kind) => truckZoneInputRefs.current.has(kind) || truckZoneRefs.current.has(kind),
    );
    if (firstDirection) {
      const zone = truckZoneRefs.current.get(firstDirection);
      zone?.scrollIntoView({ block: 'center' });
      const input = truckZoneInputRefs.current.get(firstDirection);
      if (input) {
        input.focus();
      } else {
        zone?.focus();
      }
    }
  }, [confirmConflict]);
  const [truckRemoveTargetFid, setTruckRemoveTargetFid] = useState<string | null>(null);
  const [truckRemoveReason, setTruckRemoveReason] = useState('');
  const [truckRemovingFid, setTruckRemovingFid] = useState<string | null>(null);
  // Bulk warehouse assignment — selected package ids.
  const [selectedPkgIds, setSelectedPkgIds] = useState<Set<string>>(new Set());
  const bulkAssign = useBulkAssignWarehouse();
  const deletePackage = useDeleteInboundPackage();
  const [lotDeleteOpen, setLotDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<InboundPackageDto | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  // Reason-prompt modal state for bulk assignments that touch completed packages.
  const [reasonModal, setReasonModal] = useState<{
    open: boolean;
    warehouseId: string | null;
    warehouseLabel: string;
    affectedCount: number;
  }>({ open: false, warehouseId: null, warehouseLabel: '', affectedCount: 0 });
  const [reasonText, setReasonText] = useState('');
  // Generic photo preview modal — used by both per-package and truck
  // panorama clicks. Holds the title to render in the dialog header plus
  // the list of evidence_file_ids to render as thumbnails.
  const [photoPreview, setPhotoPreview] = useState<{
    title: string;
    subtitle?: string;
    fids: string[];
  } | null>(null);

  // i18n label map for the field names recorded in change_summary.
  const fieldLabel = (f: string): string => {
    switch (f) {
      case 'route_type':
        return t('efs.operations.sorting.fields.route_type');
      case 'incoming_item_id':
      case 'raw_incoming_item_name':
        return t('efs.operations.inbound.package_edit.incoming_item');
      case 'package_type_id':
        return t('efs.operations.inbound.fields.package_type');
      case 'warehouse_id':
        return t('efs.operations.inbound.package.warehouse');
      case 'gross_kg':
        return t('efs.operations.inbound.fields.gross_kg');
      case 'tare_kg':
        return t('efs.operations.inbound.fields.tare_kg');
      case 'net_kg':
        return t('efs.operations.inbound.fields.net_kg');
      case 'notes':
        return t('efs.operations.inbound.package_edit.notes');
      case 'photo_evidence_file_ids':
        return t('efs.operations.inbound.package.photos');
      case 'status':
        return t('efs.operations.inbound.fields.status');
      default:
        return f;
    }
  };
  const revisionValueLabel = (field: string, value: unknown): string => {
    if (value == null) return '—';
    const text = String(value);
    const unavailable = t('efs.operations.inbound.fields.value_unavailable', {
      defaultValue: '名称を取得できません',
    });
    if (field === 'route_type') {
      return t(`efs.masters.route_type.${text}`, { ns: 'masters', defaultValue: text });
    }
    if (field === 'status') {
      return t(`efs.operations.inbound.lot_detail.status_${text}`, {
        defaultValue: text,
      });
    }
    if (field === 'package_type_id') return packageTypeMap.get(text) ?? unavailable;
    if (field === 'warehouse_id') return warehouseMap.get(text) ?? unavailable;
    return text;
  };

  const lot = query.data;
  const canonicalBatchId = lot?.monthly_batch_id ?? batchId;

  useEffect(() => {
    if (!lot?.monthly_batch_id || !lotId || batchId === lot.monthly_batch_id) return;
    navigate(`/batches/${lot.monthly_batch_id}/inbound/${lotId}`, { replace: true });
  }, [batchId, lot?.monthly_batch_id, lotId, navigate]);

  const activePackages = useMemo(
    () => (lot?.packages ?? []).filter((p) => p.status !== 'deleted'),
    [lot],
  );
  // Units minted from a dismantling intermediate sit in this lot only because
  // `inbound_lot_id` is NOT NULL. They are a product of dismantling the lot's
  // other units — not part of what arrived — so this page leaves them out
  // entirely; they belong to (and are managed from) the dismantling flow.
  const receivedPackages = useMemo(
    () => activePackages.filter((p) => !p.derived_from_dismantling),
    [activePackages],
  );
  /** Rows to render: received units only, deleted ones included (struck through). */
  const visiblePackages = useMemo(
    () => (lot?.packages ?? []).filter((p) => !p.derived_from_dismantling),
    [lot],
  );
  const pendingCount = useMemo(
    () => receivedPackages.filter((p) => p.status === 'pending').length,
    [receivedPackages],
  );

  const truckPhotosCount = (lot?.truck_photo_evidence_file_ids ?? []).length;
  // FR-004 Part B — per-kind counts over LINKED photos. Confirmation is based
  // on left/right/rear coverage, not raw photo count. Untagged legacy photos
  // must be classified from this screen before the lot can be confirmed.
  const truckKindCounts = useMemo(() => {
    const kinds = lot?.truck_photo_kinds ?? {};
    const counts: Record<TruckPhotoKind, number> = { left: 0, right: 0, rear: 0, other: 0 };
    (lot?.truck_photo_evidence_file_ids ?? []).forEach((fid) => {
      const k = kinds[fid] as TruckPhotoKind | undefined;
      if (k && counts[k] !== undefined) counts[k] += 1;
    });
    return counts;
  }, [lot]);
  const unclassifiedTruckPhotoIds = useMemo(() => {
    const kinds = lot?.truck_photo_kinds ?? {};
    return (lot?.truck_photo_evidence_file_ids ?? []).filter((fid) => {
      const kind = kinds[fid] as TruckPhotoKind | undefined;
      return !kind || !truckPhotoKindValues.includes(kind);
    });
  }, [lot]);
  const unclassifiedTruckPhotoCount = unclassifiedTruckPhotoIds.length;
  const duplicateTruckPhotoIds = useMemo(
    () => lot?.duplicate_truck_photo_evidence_file_ids ?? [],
    [lot?.duplicate_truck_photo_evidence_file_ids],
  );
  const duplicateTruckPhotoIdSet = useMemo(
    () => new Set(duplicateTruckPhotoIds),
    [duplicateTruckPhotoIds],
  );
  const duplicateTruckPhotoCount = duplicateTruckPhotoIds.length;
  const truckPhotosByKind = useMemo(() => {
    const grouped: Record<RequiredTruckPhotoKind, string[]> & { supplemental: string[] } = {
      left: [],
      right: [],
      rear: [],
      supplemental: [],
    };
    const kinds = lot?.truck_photo_kinds ?? {};
    (lot?.truck_photo_evidence_file_ids ?? []).forEach((fid) => {
      const kind = kinds[fid] as TruckPhotoKind | undefined;
      if (kind === 'left' || kind === 'right' || kind === 'rear') grouped[kind].push(fid);
      else grouped.supplemental.push(fid);
    });
    return grouped;
  }, [lot]);
  const truckCoverageCount = REQUIRED_TRUCK_KINDS.filter((k) => truckKindCounts[k] > 0).length;
  const missingTruckKinds = REQUIRED_TRUCK_KINDS.filter((k) => truckKindCounts[k] === 0);
  const missingTruckPhotos = missingTruckKinds.length > 0;
  const missingTruckKindLabels = missingTruckKinds
    .map((k) =>
      t(`efs.operations.inbound.truck_photos.kind.${k}`, {
        defaultValue: k,
      }),
    )
    .join(' / ');

  // Resolve all UUID FK fields to human-readable names (operators shouldn't
  // see raw UUIDs). We piggy-back on the master caches the create / edit
  // dialogs already populate.
  const suppliersQuery = useSuppliersList({ active_only: false, per_page: 500 });
  const supplierMap = useMemo(() => {
    const m = new Map<string, string>();
    (suppliersQuery.data?.rows ?? []).forEach((s) => m.set(s.company_id, s.display_name));
    return m;
  }, [suppliersQuery.data]);
  const supplierName = lot?.source_company_id
    ? (supplierMap.get(lot.source_company_id) ?? lot.source_company_id)
    : '—';

  const packageTypesQuery = usePackageTypesList({ active: true, per_page: 500 });
  const packageTypeMap = useMemo(() => {
    const m = new Map<string, string>();
    (packageTypesQuery.data?.rows ?? []).forEach((p) => m.set(p.package_type_id, p.display_name));
    return m;
  }, [packageTypesQuery.data]);
  const packageTypeOptions = useMemo(
    () =>
      (packageTypesQuery.data?.rows ?? [])
        .map((p) => `${p.display_name} (${Number(p.default_tare_kg).toFixed(1)} kg)`)
        .join(' / '),
    [packageTypesQuery.data],
  );

  // Scope the picker to this batch's facility: the backend refuses a warehouse
  // from anywhere else, so listing them all would only offer failing choices.
  const batchFacilityId = useBatchFacilityId(canonicalBatchId);
  const warehousesQuery = useWarehousesList(
    { active_only: true, per_page: 500, facility_id: batchFacilityId },
    { enabled: !!batchFacilityId },
  );
  const warehouses = useMemo(() => warehousesQuery.data?.rows ?? [], [warehousesQuery.data]);
  const warehouseMap = useMemo(() => {
    const m = new Map<string, string>();
    warehouses.forEach((w) => m.set(w.warehouse_id, `${w.code} — ${w.display_name}`));
    return m;
  }, [warehouses]);
  const warehouseOptions = useMemo(
    () => warehouses.map((w) => `${w.code} — ${w.display_name}`).join(' / '),
    [warehouses],
  );

  // Sanity-check the truck-weighbridge "計量正味" against the sum of every
  // package's *gross_kg* (each package's gross still includes its container
  // tare — the weighbridge cargo total has not yet deducted those tares,
  // so per-package gross is the right comparison basis. Comparing against
  // Σ net_kg would always show a drift equal to Σ tare, which is expected
  // and not an anomaly.) 1% drift = amber warning.
  const measuredNet = lot?.measured_net_kg ? Number(lot.measured_net_kg) : null;
  const sumPackageGrossKg = useMemo(() => {
    if (!lot) return null;
    let sum = 0;
    for (const p of receivedPackages) {
      if (p.gross_kg) sum += Number(p.gross_kg);
    }
    return sum;
  }, [receivedPackages, lot]);
  const allCompleted =
    !!lot && receivedPackages.length > 0 && receivedPackages.every((p) => p.status === 'completed');
  const netDriftPct =
    measuredNet && measuredNet > 0 && sumPackageGrossKg != null
      ? Math.abs(measuredNet - sumPackageGrossKg) / measuredNet
      : null;
  const netDriftWarn = allCompleted && netDriftPct != null && netDriftPct > 0.01;

  const canConfirmInboundLot = hasPermission(currentUser, 'receiving.first_inbound.confirm');
  const canManageEvidence = hasPermission(currentUser, 'evidence.manage');
  const canManageInbound = hasPermission(currentUser, 'inbound.manage');
  const canReturnLinkedDispatch = hasPermission(currentUser, 'dispatch.request.fulfill');
  const canCancelInboundLot =
    !!lot && canManageInbound && (lot.status === 'imported' || lot.status === 'confirmed');
  const canRequestWeightCorrection = hasPermission(currentUser, 'correction.request');
  const canApplyWeightCorrection = hasPermission(currentUser, 'correction.approve');
  const canViewTechnicalErrors = hasPermission(currentUser, 'system.admin');
  const canClassifyTruckPhotos = canConfirmInboundLot || canManageInbound;
  const canUploadTruckPhotos = canManageEvidence && canClassifyTruckPhotos;
  const canRemoveImportedTruckPhotos = !!lot && lot.status === 'imported' && canUploadTruckPhotos;
  const canRemoveConfirmedDuplicateTruckPhotos =
    !!lot && lot.status === 'confirmed' && canUploadTruckPhotos;
  const canUseFileLibrary =
    fileLibraryEnabled &&
    canUploadTruckPhotos &&
    (hasPermission(currentUser, 'file_library.read') ||
      hasPermission(currentUser, 'file_library.manage'));
  const localizedMutationError = (
    error: unknown,
    fallbackKey: string,
    defaultValue: string,
  ): string => {
    const text = localizeApiError((key, opts) => t(key, { ns: 'issues', ...(opts ?? {}) }), error);
    return text.cataloged ? text.message : t(fallbackKey, { defaultValue });
  };
  const confirmDisabledReason = !lot
    ? t('efs.common.messages.loading', { ns: 'common', defaultValue: '読み込み中…' })
    : lot.status !== 'imported'
      ? t('efs.operations.inbound.lot_detail.confirm_disabled_status', {
          status: t(`efs.operations.statuses.${lot.status}`, { defaultValue: lot.status }),
          defaultValue: 'このロットは現在 {{status}} のため確定できません',
        })
      : !canConfirmInboundLot
        ? t('efs.operations.inbound.lot_detail.confirm_disabled_no_permission', {
            defaultValue: '現在のロールには初回入庫確認権限がありません',
          })
        : pendingCount > 0
          ? t('efs.operations.inbound.lot_detail.confirm_disabled_pending')
          : unclassifiedTruckPhotoCount > 0
            ? t('efs.operations.inbound.lot_detail.confirm_disabled_unclassified_photos', {
                count: unclassifiedTruckPhotoCount,
                defaultValue:
                  '未分類のトラック写真が {{count}} 件あります。該当カードをその他に分類するか、この入荷から外してください。',
              })
            : duplicateTruckPhotoCount > 0
              ? t('efs.operations.inbound.lot_detail.confirm_disabled_duplicate_photos', {
                  count: duplicateTruckPhotoCount,
                  defaultValue:
                    '重複したトラック写真が {{count}} 件あります。赤い重複カードから余分な写真を外してください。',
                })
              : missingTruckPhotos
                ? t('efs.operations.inbound.lot_detail.confirm_disabled_no_photos', {
                    count: truckPhotosCount,
                    missing: missingTruckKindLabels,
                  })
                : confirm.isPending
                  ? t('efs.common.messages.saving', { ns: 'common', defaultValue: '保存中…' })
                  : null;
  const confirmDisabled = !!confirmDisabledReason;

  const openEdit = (pkg: InboundPackageDto) => {
    setEditPkg(pkg);
    setEditOpen(true);
  };

  const openWeightCorrection = (pkg: InboundPackageDto) => {
    setWeightCorrectionPkg(pkg);
  };

  const openDelete = (pkg: InboundPackageDto) => {
    setDeleteTarget(pkg);
    setDeleteReason('');
  };

  const submitDelete = async () => {
    if (!deleteTarget || !deleteReason.trim()) return;
    await deletePackage.mutateAsync({
      packageId: deleteTarget.inbound_package_id,
      body: { reason: deleteReason.trim() },
    });
    setDeleteTarget(null);
    setDeleteReason('');
    setSelectedPkgIds((prev) => {
      const next = new Set(prev);
      next.delete(deleteTarget.inbound_package_id);
      return next;
    });
  };

  const togglePkgSelected = (pkgId: string) => {
    setSelectedPkgIds((prev) => {
      const next = new Set(prev);
      if (next.has(pkgId)) next.delete(pkgId);
      else next.add(pkgId);
      return next;
    });
  };

  const runBulkAssign = async (warehouseId: string | null, change_reason?: string) => {
    if (!lot || selectedPkgIds.size === 0) return;
    await bulkAssign.mutateAsync({
      lotId: lot.inbound_lot_id,
      body: {
        package_ids: Array.from(selectedPkgIds),
        warehouse_id: warehouseId,
        change_reason,
      },
    });
    setSelectedPkgIds(new Set());
  };

  const handleBulkAssign = async (warehouseId: string | null, warehouseLabel: string) => {
    if (!lot || selectedPkgIds.size === 0) return;
    const selected = receivedPackages.filter((p) => selectedPkgIds.has(p.inbound_package_id));
    // Count completed packages whose warehouse_id would actually change.
    const affected = selected.filter(
      (p) => p.status === 'completed' && (p.warehouse_id ?? null) !== warehouseId,
    );
    if (affected.length > 0) {
      // Open the inline reason modal — never relies on window.prompt
      // (which the in-app preview blocks).
      setReasonText('');
      setReasonModal({
        open: true,
        warehouseId,
        warehouseLabel,
        affectedCount: affected.length,
      });
      return;
    }
    await runBulkAssign(warehouseId);
  };

  const handleTruckUpload = async (kind: TruckPhotoKind, files: FileList) => {
    if (!lot || !canonicalBatchId || !canUploadTruckPhotos) return;
    setTruckUploadingKinds((current) => ({ ...current, [kind]: true }));
    setTruckUploadErrors((current) => {
      const next = { ...current };
      delete next[kind];
      return next;
    });
    try {
      for (const file of Array.from(files)) {
        const ev = await uploadEvidence(canonicalBatchId, file, 'photo');
        await linkTruckPhotoMutation.mutateAsync({
          lotId: lot.inbound_lot_id,
          evidenceFileId: ev.evidence_file_id,
          kind,
        });
      }
      await query.refetch();
    } catch (err) {
      setTruckUploadErrors((current) => ({
        ...current,
        [kind]: localizedMutationError(
          err,
          'efs.operations.inbound.errors.truck_photo_upload_failed',
          'トラック写真を追加できませんでした。時間をおいて再試行してください。',
        ),
      }));
      await query.refetch();
    } finally {
      setTruckUploadingKinds((current) => ({ ...current, [kind]: false }));
    }
  };

  const handleTruckLibrarySelect = async (kind: TruckPhotoKind, file: FileLibrarySelection) => {
    if (!lot || !canonicalBatchId || !canUseFileLibrary) return;
    setTruckUploadingKinds((current) => ({ ...current, [kind]: true }));
    setTruckUploadErrors((current) => {
      const next = { ...current };
      delete next[kind];
      return next;
    });
    try {
      const ev = await attachInboundEvidenceFromLibrary(
        canonicalBatchId,
        file.company_file_id,
        'photo',
      );
      await linkTruckPhotoMutation.mutateAsync({
        lotId: lot.inbound_lot_id,
        evidenceFileId: ev.evidence_file_id,
        kind,
      });
      await query.refetch();
    } catch (err) {
      setTruckUploadErrors((current) => ({
        ...current,
        [kind]: localizedMutationError(
          err,
          'efs.operations.inbound.errors.truck_photo_attach_failed',
          'ファイル庫の写真を追加できませんでした。時間をおいて再試行してください。',
        ),
      }));
      await query.refetch();
    } finally {
      setTruckUploadingKinds((current) => ({ ...current, [kind]: false }));
    }
  };

  const handleTruckKindChange = async (evidenceFileId: string, kind: TruckPhotoKind) => {
    if (!lot) return;
    setTruckKindUpdatingFid(evidenceFileId);
    setTruckError(null);
    try {
      await setTruckKindMutation.mutateAsync({
        lotId: lot.inbound_lot_id,
        evidenceFileId,
        kind,
      });
      await query.refetch();
    } catch (err) {
      setTruckError(
        localizedMutationError(
          err,
          'efs.operations.inbound.errors.truck_photo_classify_failed',
          '写真分類を変更できませんでした。時間をおいて再試行してください。',
        ),
      );
    } finally {
      setTruckKindUpdatingFid(null);
    }
  };

  const handleTruckPhotoRemove = async () => {
    if (!lot || !truckRemoveTargetFid) return;
    const evidenceFileId = truckRemoveTargetFid;
    const isDuplicate = duplicateTruckPhotoIdSet.has(evidenceFileId);
    const canRemove =
      canRemoveImportedTruckPhotos || (canRemoveConfirmedDuplicateTruckPhotos && isDuplicate);
    if (!canRemove) return;
    const cleanedReason = truckRemoveReason.trim();
    if (lot.status === 'confirmed' && !cleanedReason) {
      setTruckError(
        t('efs.operations.inbound.truck_photos.remove_reason_required', {
          defaultValue: '確定済みロットから重複写真を外すには変更理由が必要です。',
        }),
      );
      return;
    }
    setTruckRemovingFid(evidenceFileId);
    setTruckError(null);
    try {
      await removeTruckPhotoMutation.mutateAsync({
        lotId: lot.inbound_lot_id,
        evidenceFileId,
        changeReason:
          lot.status === 'confirmed'
            ? cleanedReason
            : cleanedReason ||
              t('efs.operations.inbound.truck_photos.remove_default_reason', {
                defaultValue: 'Removed truck photo from inbound lot',
              }),
      });
      setTruckRemoveTargetFid(null);
      setTruckRemoveReason('');
      await query.refetch();
    } catch (err) {
      setTruckError(
        localizedMutationError(
          err,
          'efs.operations.inbound.errors.truck_photo_remove_failed',
          'トラック写真をこの入荷から外せませんでした。時間をおいて再試行してください。',
        ),
      );
    } finally {
      setTruckRemovingFid(null);
    }
  };

  const renderTruckPhotoCard = (
    fid: string,
    index: number,
    previewFids: string[],
    groupLabel: string,
  ) => {
    const kind = (lot?.truck_photo_kinds ?? {})[fid] as TruckPhotoKind | undefined;
    const isUnclassified = !kind || !truckPhotoKindValues.includes(kind);
    const isDuplicate = duplicateTruckPhotoIdSet.has(fid);
    const canRemoveThisTruckPhoto =
      canRemoveImportedTruckPhotos || (canRemoveConfirmedDuplicateTruckPhotos && isDuplicate);
    const kindLabel = kind
      ? t(`efs.operations.inbound.truck_photos.kind.${kind}`, { defaultValue: kind })
      : t('efs.operations.inbound.truck_photos.kind_unset', { defaultValue: '未分類' });
    const photoLabel = t('efs.operations.inbound.truck_photos.photo_position_label', {
      kind: groupLabel,
      index: index + 1,
      defaultValue: '{{kind}}の写真 {{index}}',
    });
    const cardToneClass = isDuplicate
      ? 'border-red-500 bg-red-50/80 dark:border-red-400 dark:bg-red-950'
      : isUnclassified
        ? 'border-amber-500 bg-amber-50/80 dark:border-amber-400 dark:bg-amber-950'
        : 'border-efs-border bg-efs-bg-subtle';
    // Server confirm conflict pointing at this exact photo (spec-13 §8.7).
    const isConfirmConflict = !!confirmConflict?.evidenceIds.has(fid);
    return (
      <div
        key={fid}
        className={`min-w-0 space-y-1 ${isConfirmConflict ? 'rounded-md ring-2 ring-red-500' : ''}`}
      >
        <button
          type="button"
          ref={(el) => {
            if (el) truckCardRefs.current.set(fid, el);
            else truckCardRefs.current.delete(fid);
          }}
          onClick={() =>
            setPhotoPreview({
              title: `${t('efs.operations.inbound.truck_photos.label')} — ${groupLabel}`,
              subtitle: lot?.lot_code,
              fids: previewFids,
            })
          }
          className={`group relative aspect-[4/3] w-full overflow-hidden rounded-md border transition hover:border-efs-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${cardToneClass}`}
          title={t('efs.operations.inbound.truck_photos.preview_for_kind', {
            kind: groupLabel,
            index: index + 1,
            defaultValue: '{{kind}}の写真 {{index}} をプレビュー',
          })}
          aria-label={t('efs.operations.inbound.truck_photos.preview_for_kind', {
            kind: groupLabel,
            index: index + 1,
            defaultValue: '{{kind}}の写真 {{index}} をプレビュー',
          })}
        >
          <EvidenceThumb fid={fid} alt={photoLabel} />
          <span className="absolute left-1 top-1 rounded bg-black/60 px-1 text-[10px] font-semibold leading-tight text-white">
            {kindLabel}
          </span>
          {isUnclassified || isDuplicate ? (
            <span className="absolute right-1 top-1 rounded-full bg-white/90 p-1 text-red-900 shadow-sm dark:bg-black/70 dark:text-red-100">
              <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
            </span>
          ) : null}
        </button>
        {isConfirmConflict && confirmConflict ? (
          <TruckPhotoConflictNotice code={confirmConflict.code} />
        ) : null}
        {isUnclassified ? (
          <Alert variant="warning" size="inline">
            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
            <span>
              {t('efs.operations.inbound.truck_photos.unclassified_card_warning', {
                label: photoLabel,
                defaultValue:
                  '{{label}} は未分類です。その他に分類するか、この入荷から外してください。',
              })}
            </span>
          </Alert>
        ) : null}
        {isDuplicate ? (
          <Alert variant="danger" size="inline">
            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
            <span>
              {t('efs.operations.inbound.truck_photos.duplicate_card_warning', {
                label: photoLabel,
                defaultValue:
                  '{{label}} は別のトラック写真と同じ内容です。余分なカードを外してください。',
              })}
            </span>
          </Alert>
        ) : null}
        {lot?.status === 'imported' && canClassifyTruckPhotos ? (
          <select
            value={kind ?? ''}
            disabled={truckKindUpdatingFid === fid || truckRemovingFid === fid}
            onChange={(event) => {
              const nextKind = event.target.value as TruckPhotoKind;
              if (nextKind) void handleTruckKindChange(fid, nextKind);
            }}
            aria-label={t('efs.operations.inbound.truck_photos.reclassify_for_kind', {
              kind: groupLabel,
              index: index + 1,
              defaultValue: '{{kind}}の写真 {{index}} の分類を変更',
            })}
            className="h-8 w-full rounded-md border border-efs-border bg-efs-bg-panel px-1 text-xs"
          >
            <option value="" disabled>
              {t('efs.operations.inbound.truck_photos.kind_unset', {
                defaultValue: '未分類',
              })}
            </option>
            {truckPhotoKindValues.map((nextKind) => (
              <option key={nextKind} value={nextKind}>
                {t(`efs.operations.inbound.truck_photos.kind.${nextKind}`, {
                  defaultValue: nextKind,
                })}
              </option>
            ))}
          </select>
        ) : null}
        {canRemoveThisTruckPhoto ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={truckRemovingFid === fid}
            onClick={() => {
              setTruckRemoveTargetFid(fid);
              setTruckRemoveReason('');
              setTruckError(null);
            }}
            className="h-7 w-full justify-center px-2 text-xs text-red-900 hover:text-red-800 dark:text-red-100 dark:hover:text-red-200"
          >
            {truckRemovingFid === fid ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
            <Trash2 className="mr-1 h-3 w-3" />
            {t('efs.operations.inbound.truck_photos.remove_from_lot', {
              defaultValue: 'この入荷から外す',
            })}
          </Button>
        ) : null}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.operations.inbound.lot_detail.title')}
        subtitle={lot ? lot.lot_code : undefined}
        actions={
          lot
            ? (() => {
                const totalPkgs = receivedPackages.length;
                const completedPkgs = totalPkgs - pendingCount;
                // Required units: 1 per package + 3 truck photos (left/right/rear).
                const requiredUnits = totalPkgs + REQUIRED_TRUCK_PHOTO_COUNT;
                const doneUnits = completedPkgs + truckCoverageCount;
                const overallPct =
                  requiredUnits > 0 ? Math.round((doneUnits / requiredUnits) * 100) : 0;
                const allReady = doneUnits === requiredUnits;
                return (
                  <div className="flex items-center gap-3">
                    <Button type="button" variant="outline" onClick={() => window.print()}>
                      <Printer className="mr-1 h-4 w-4" />
                      {t('efs.operations.inbound.actions.print_worksheet')}
                    </Button>
                    {lot.status === 'imported' || canCancelInboundLot ? (
                      <>
                        {lot.status === 'imported' ? (
                          <>
                            <span
                              className={
                                'rounded-full px-3 py-1 text-sm font-mono ' +
                                (allReady
                                  ? 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100'
                                  : 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-100')
                              }
                              title={t('efs.operations.inbound.lot_detail.progress_tooltip', {
                                done: doneUnits,
                                total: requiredUnits,
                                defaultValue: '完了 {{done}} / {{total}}（包装＋トラック写真）',
                              })}
                            >
                              {overallPct}%
                            </span>
                            <Button
                              onClick={() => confirm.mutate(lot.inbound_lot_id)}
                              disabled={confirmDisabled}
                              title={confirmDisabledReason ?? undefined}
                            >
                              <CheckCircle className="mr-1 h-4 w-4" />
                              {t('efs.operations.inbound.lot_detail.confirm')}
                            </Button>
                            {confirmDisabledReason ? (
                              <span className="max-w-xs text-xs text-amber-900 dark:text-amber-100">
                                {confirmDisabledReason}
                              </span>
                            ) : null}
                            {confirm.error ? (
                              <LocalizedApiError
                                error={confirm.error}
                                showTechnicalDetails={canViewTechnicalErrors}
                                className="max-w-md text-left"
                              />
                            ) : null}
                          </>
                        ) : null}
                        {canCancelInboundLot ? (
                          <Button
                            type="button"
                            variant="outline"
                            disabled={!!lot.linked_dispatch_request_id && !canReturnLinkedDispatch}
                            title={
                              lot.linked_dispatch_request_id && !canReturnLinkedDispatch
                                ? t('efs.operations.inbound.lot_delete.dispatch_permission_hint', {
                                    defaultValue: '配車へ戻す権限がありません。',
                                  })
                                : undefined
                            }
                            onClick={() => setLotDeleteOpen(true)}
                          >
                            <RotateCcw className="mr-1 h-4 w-4" />
                            {t('efs.operations.inbound.lot_delete.action', {
                              defaultValue: '入荷取消',
                            })}
                          </Button>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                );
              })()
            : null
        }
      />

      {lot ? (
        <PrintInboundWorksheet
          lot={lot}
          packages={receivedPackages}
          supplierName={supplierName}
          packageTypeOptions={packageTypeOptions}
          warehouseOptions={warehouseOptions}
          packageTypeMap={packageTypeMap}
          warehouseMap={warehouseMap}
        />
      ) : null}

      {query.isLoading ? (
        <p className="text-sm text-efs-text-muted">{t('efs.common.messages.loading')}</p>
      ) : !lot ? (
        <p className="text-sm text-efs-text-muted">{t('efs.common.messages.empty')}</p>
      ) : (
        <>
          <div className="rounded-md border border-efs-border bg-efs-bg-panel p-4">
            <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
              <div>
                <p className="text-xs text-efs-text-muted">
                  {t('efs.operations.inbound.fields.lot_code')}
                </p>
                <p className="font-mono">{lot.lot_code}</p>
              </div>
              <div>
                <p className="text-xs text-efs-text-muted">
                  {t('efs.operations.inbound.fields.inbound_date')}
                </p>
                <p>{lot.inbound_date}</p>
              </div>
              <div>
                <p className="text-xs text-efs-text-muted">
                  {t('efs.operations.inbound.fields.status')}
                </p>
                <LotStatusBadge status={lot.status} />
              </div>
              <div>
                <p className="text-xs text-efs-text-muted">
                  {t('efs.operations.inbound.fields.vehicle')}
                </p>
                <p>{lot.vehicle ?? '—'}</p>
              </div>
              <div>
                <p className="text-xs text-efs-text-muted">
                  {t('efs.operations.inbound.fields.source_company')}
                </p>
                <p className="text-sm">{supplierName}</p>
              </div>
              <div>
                <p className="text-xs text-efs-text-muted">
                  {t('efs.operations.inbound.fields.total_kg')}
                </p>
                <KgValue value={lot.total_kg} />
              </div>
            </div>

            <fieldset className="mt-4">
              <legend className="text-xs font-semibold text-efs-text-muted">
                {t('efs.operations.inbound.truck_photos.label')}
              </legend>
              <p className="text-xs text-efs-text-muted">
                {t('efs.operations.inbound.truck_photos.kind_status', {
                  defaultValue: '左 {{left}} / 右 {{right}} / 後 {{rear}}',
                  left: truckKindCounts.left,
                  right: truckKindCounts.right,
                  rear: truckKindCounts.rear,
                })}
              </p>
              <p className="mb-3 text-xs text-efs-text-muted">
                {t('efs.operations.inbound.truck_photos.direction_upload_hint', {
                  defaultValue: '確定には左側・右側・後方が各1件必要です。',
                })}
              </p>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                {REQUIRED_TRUCK_KINDS.map((kind) => {
                  const fids = truckPhotosByKind[kind];
                  const kindLabel = t(`efs.operations.inbound.truck_photos.kind.${kind}`, {
                    defaultValue: kind,
                  });
                  const uploading = !!truckUploadingKinds[kind];
                  const uploadLabel = t('efs.operations.inbound.truck_photos.add_for_kind', {
                    kind: kindLabel,
                    defaultValue: '{{kind}}の写真・動画を選択',
                  });
                  // Server-declared missing direction → localized alert inside
                  // the affected zone (spec-13 §8.7).
                  const directionConflict =
                    confirmConflict?.code === 'TRUCK_PHOTO_DIRECTION_MISSING' &&
                    confirmConflict.missingDirections.includes(kind)
                      ? t('efs.operations.inbound.truck_photos.direction_missing_server', {
                          kind: kindLabel,
                          defaultValue:
                            '{{kind}}の写真が不足しているため確定できません。写真を追加してください。',
                        })
                      : null;
                  return (
                    <div
                      key={kind}
                      ref={(el) => {
                        if (el) truckZoneRefs.current.set(kind, el);
                        else truckZoneRefs.current.delete(kind);
                      }}
                      className="min-w-0"
                    >
                      <TruckPhotoDirectionZone
                        uploadInputRef={(el) => {
                          if (el) truckZoneInputRefs.current.set(kind, el);
                          else truckZoneInputRefs.current.delete(kind);
                        }}
                        title={kindLabel}
                        statusLabel={
                          uploading
                            ? t('efs.operations.inbound.truck_photos.status_uploading', {
                                defaultValue: 'アップロード中…',
                              })
                            : fids.length > 0
                              ? t('efs.operations.inbound.truck_photos.status_registered', {
                                  count: fids.length,
                                  defaultValue: '登録済み {{count}}件',
                                })
                              : t('efs.operations.inbound.truck_photos.status_missing', {
                                  defaultValue: '未登録',
                                })
                        }
                        hasContent={fids.length > 0}
                        uploading={uploading}
                        canUpload={lot.status === 'imported' && canUploadTruckPhotos}
                        sourceMode={truckSourceModes[kind]}
                        canUseLibrary={canUseFileLibrary}
                        libraryLabel={t('efs.file_library.picker.title', {
                          ns: 'fileLibrary',
                          defaultValue: 'ファイル庫から選択',
                        })}
                        onSourceModeChange={(mode) =>
                          setTruckSourceModes((current) => ({ ...current, [kind]: mode }))
                        }
                        onLibrarySelectClick={() => setTruckLibraryPickerKind(kind)}
                        uploadLabel={uploadLabel}
                        inputLabel={uploadLabel}
                        accept={EVIDENCE_MEDIA_ACCEPT}
                        multiple
                        error={truckUploadErrors[kind] ?? directionConflict}
                        onFilesSelected={(files) => void handleTruckUpload(kind, files)}
                      >
                        {fids.length > 0 ? (
                          <div className="grid grid-cols-2 gap-2">
                            {fids.map((fid, index) =>
                              renderTruckPhotoCard(fid, index, fids, kindLabel),
                            )}
                          </div>
                        ) : (
                          <p className="text-xs text-efs-text-muted">
                            {t('efs.operations.inbound.truck_photos.direction_empty_hint', {
                              kind: kindLabel,
                              defaultValue: '{{kind}}が写る写真を選択してください。',
                            })}
                          </p>
                        )}
                      </TruckPhotoDirectionZone>
                    </div>
                  );
                })}
              </div>

              <div className="mt-3">
                {(() => {
                  const fids = truckPhotosByKind.supplemental;
                  const supplementalLabel = t(
                    'efs.operations.inbound.truck_photos.supplemental_label',
                    { defaultValue: '補足写真' },
                  );
                  const uploading = !!truckUploadingKinds.other;
                  const uploadLabel = t('efs.operations.inbound.truck_photos.add_supplemental', {
                    defaultValue: '補足写真・動画を選択',
                  });
                  return (
                    <TruckPhotoDirectionZone
                      title={supplementalLabel}
                      statusLabel={
                        uploading
                          ? t('efs.operations.inbound.truck_photos.status_uploading', {
                              defaultValue: 'アップロード中…',
                            })
                          : t('efs.operations.inbound.truck_photos.status_optional', {
                              count: fids.length,
                              defaultValue: '任意 {{count}}件',
                            })
                      }
                      hasContent={fids.length > 0}
                      optional
                      uploading={uploading}
                      canUpload={lot.status === 'imported' && canUploadTruckPhotos}
                      sourceMode={truckSourceModes.other}
                      canUseLibrary={canUseFileLibrary}
                      libraryLabel={t('efs.file_library.picker.title', {
                        ns: 'fileLibrary',
                        defaultValue: 'ファイル庫から選択',
                      })}
                      onSourceModeChange={(mode) =>
                        setTruckSourceModes((current) => ({ ...current, other: mode }))
                      }
                      onLibrarySelectClick={() => setTruckLibraryPickerKind('other')}
                      uploadLabel={uploadLabel}
                      inputLabel={uploadLabel}
                      accept={EVIDENCE_MEDIA_ACCEPT}
                      multiple
                      error={truckUploadErrors.other}
                      onFilesSelected={(files) => void handleTruckUpload('other', files)}
                    >
                      {fids.length > 0 ? (
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 md:grid-cols-6">
                          {fids.map((fid, index) =>
                            renderTruckPhotoCard(fid, index, fids, supplementalLabel),
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-efs-text-muted">
                          {t('efs.operations.inbound.truck_photos.supplemental_hint', {
                            defaultValue: '確定条件には含まれません。',
                          })}
                        </p>
                      )}
                    </TruckPhotoDirectionZone>
                  );
                })()}
              </div>

              {truckError ? (
                <p role="alert" className="mt-2 text-xs text-red-900 dark:text-red-100">
                  {truckError}
                </p>
              ) : null}
              <FileLibraryPickerDialog
                open={!!truckLibraryPickerKind}
                onOpenChange={(nextOpen) => {
                  if (!nextOpen) setTruckLibraryPickerKind(null);
                }}
                batchId={batchId}
                acceptMimeGroups={EVIDENCE_MEDIA_MIME_GROUPS}
                acceptFile={fileMatchesEvidenceMediaAccept}
                onSelect={(file) => {
                  if (truckLibraryPickerKind) {
                    void handleTruckLibrarySelect(truckLibraryPickerKind, file);
                  }
                }}
              />
              {unclassifiedTruckPhotoCount > 0 ? (
                <p className="mt-2 text-xs text-amber-900 dark:text-amber-100">
                  {t('efs.operations.inbound.truck_photos.unclassified_warning', {
                    count: unclassifiedTruckPhotoCount,
                    defaultValue:
                      '未分類のトラック写真が {{count}} 件あります。該当カードをその他に分類するか、この入荷から外してください。',
                  })}
                </p>
              ) : null}
              {duplicateTruckPhotoCount > 0 ? (
                <p className="mt-2 text-xs text-red-900 dark:text-red-100">
                  {t('efs.operations.inbound.truck_photos.duplicate_warning', {
                    count: duplicateTruckPhotoCount,
                    defaultValue:
                      '重複したトラック写真が {{count}} 件あります。赤い重複カードから余分な写真を外してください。',
                  })}
                </p>
              ) : null}
              {missingTruckPhotos ? (
                <p className="mt-2 text-xs text-amber-900 dark:text-amber-100">
                  {t('efs.operations.inbound.truck_photos.missing_kinds', {
                    missing: missingTruckKindLabels,
                    defaultValue: '不足: {{missing}}',
                  })}
                </p>
              ) : null}
            </fieldset>
          </div>

          {/* Truck-arrival worksheet — always shown, with edit button + audit
              log. `*` is rendered next to fields that have been modified
              after lot creation (per lot.revisions). */}
          {(() => {
            const lotRevs = lot.revisions ?? [];
            const wsEdited = editedFieldSet(lotRevs);
            return (
              <div className="rounded-md border border-efs-border bg-efs-bg-panel p-4">
                <div className="flex items-baseline justify-between">
                  <p className="text-sm font-semibold">
                    {t('efs.operations.inbound.worksheet.title')}
                  </p>
                  <div className="flex items-center gap-1">
                    {lotRevs.length > 0 ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setWorksheetRevOpen(true)}
                        title={t('efs.operations.inbound.revisions.button_tooltip', {
                          defaultValue: '異動紀錄を表示',
                        })}
                      >
                        <ClipboardList className="mr-1 h-3 w-3" />
                        {lotRevs.length}
                      </Button>
                    ) : null}
                    {lot.status === 'imported' ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setWorksheetEditOpen(true)}
                      >
                        <Pencil className="mr-1 h-3 w-3" />
                        {t('efs.operations.inbound.worksheet.edit', {
                          defaultValue: '編集',
                        })}
                      </Button>
                    ) : null}
                  </div>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                  <div>
                    <p className="text-xs text-efs-text-muted">
                      {t('efs.operations.inbound.worksheet.measured_net_kg')}
                    </p>
                    <p>
                      {lot.measured_net_kg ? <KgValue value={lot.measured_net_kg} /> : '—'}
                      <FieldEditedMark edited={wsEdited.has('measured_net_kg')} />
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-efs-text-muted">
                      {t('efs.operations.inbound.worksheet.pallet_count')}
                    </p>
                    <p>
                      {lot.declared_pallet_count ?? '—'}
                      <FieldEditedMark edited={wsEdited.has('declared_pallet_count')} />
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-efs-text-muted">
                      {t('efs.operations.inbound.worksheet.fureikon_count')}
                    </p>
                    <p>
                      {lot.declared_fureikon_count ?? '—'}
                      <FieldEditedMark edited={wsEdited.has('declared_fureikon_count')} />
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-efs-text-muted">
                      {t('efs.operations.inbound.worksheet.cardboard_count')}
                    </p>
                    <p>
                      {lot.declared_cardboard_count ?? '—'}
                      <FieldEditedMark edited={wsEdited.has('declared_cardboard_count')} />
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-efs-text-muted">
                      {t('efs.operations.inbound.worksheet.arrival_at')}
                    </p>
                    <p>
                      {lot.arrival_at ? formatDateTime(lot.arrival_at) : '—'}
                      <FieldEditedMark edited={wsEdited.has('arrival_at')} />
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-efs-text-muted">
                      {t('efs.operations.inbound.worksheet.departure_at')}
                    </p>
                    <p>
                      {lot.departure_at ? formatDateTime(lot.departure_at) : '—'}
                      <FieldEditedMark edited={wsEdited.has('departure_at')} />
                    </p>
                  </div>
                </div>
                {netDriftWarn ? (
                  <Alert variant="warning" size="inline" className="mt-3">
                    {t('efs.operations.inbound.worksheet.drift_warning', {
                      measured: measuredNet,
                      computed: sumPackageGrossKg,
                      pct: ((netDriftPct ?? 0) * 100).toFixed(2),
                    })}
                  </Alert>
                ) : null}
              </div>
            );
          })()}

          <div className="rounded-md border border-efs-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">
                    {lot.status === 'imported' ? (
                      <input
                        type="checkbox"
                        aria-label={t('efs.operations.inbound.bulk_assign.select_all', {
                          defaultValue: 'すべて選択',
                        })}
                        checked={
                          receivedPackages.length > 0 &&
                          selectedPkgIds.size === receivedPackages.length
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedPkgIds(
                              new Set(receivedPackages.map((p) => p.inbound_package_id)),
                            );
                          } else {
                            setSelectedPkgIds(new Set());
                          }
                        }}
                      />
                    ) : null}
                  </TableHead>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>{t('efs.operations.inbound.drawer.fields.item_name')}</TableHead>
                  <TableHead>{t('efs.operations.inbound.fields.package_type')}</TableHead>
                  <TableHead>{t('efs.operations.inbound.package.warehouse')}</TableHead>
                  <TableHead className="text-right">
                    {t('efs.operations.inbound.fields.gross_kg')}
                  </TableHead>
                  <TableHead className="text-right">
                    {t('efs.operations.inbound.fields.tare_kg')}
                  </TableHead>
                  <TableHead className="text-right">
                    {t('efs.operations.inbound.fields.net_kg')}
                  </TableHead>
                  <TableHead className="text-right">
                    {t('efs.operations.inbound.fields.status')}
                  </TableHead>
                  <TableHead className="text-right">
                    {t('efs.operations.inbound.lot_detail.revisions_header', {
                      defaultValue: '異動',
                    })}
                  </TableHead>
                  <TableHead className="text-right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {visiblePackages.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="py-6 text-center text-efs-text-muted">
                      {t('efs.common.messages.empty')}
                    </TableCell>
                  </TableRow>
                ) : (
                  visiblePackages.map((pkg) => {
                    const edited = editedFieldSet(pkg.revisions ?? []);
                    const isDeleted = pkg.status === 'deleted';
                    return (
                      <TableRow
                        key={pkg.inbound_package_id}
                        className={
                          isDeleted
                            ? 'bg-red-50/70 text-red-900 line-through dark:bg-red-950 dark:text-red-100'
                            : undefined
                        }
                      >
                        <TableCell className="w-8">
                          {lot.status === 'imported' && !isDeleted ? (
                            <input
                              type="checkbox"
                              aria-label={pkg.package_code}
                              checked={selectedPkgIds.has(pkg.inbound_package_id)}
                              onChange={() => togglePkgSelected(pkg.inbound_package_id)}
                            />
                          ) : null}
                        </TableCell>
                        <TableCell className="font-mono text-xs">{pkg.package_code}</TableCell>
                        <TableCell className="text-xs">
                          <span className="inline-flex items-center gap-1">
                            {pkg.raw_incoming_item_name}
                            <FieldEditedMark
                              edited={
                                edited.has('incoming_item_id') ||
                                edited.has('raw_incoming_item_name')
                              }
                            />
                            {(pkg.photo_evidence_file_ids ?? []).length > 0 ? (
                              <button
                                type="button"
                                onClick={() =>
                                  setPhotoPreview({
                                    title: t('efs.operations.inbound.photo_preview.title', {
                                      defaultValue: '写真プレビュー',
                                    }),
                                    subtitle: `${pkg.package_code} — ${pkg.raw_incoming_item_name}`,
                                    fids: pkg.photo_evidence_file_ids ?? [],
                                  })
                                }
                                className="inline-flex items-center gap-0.5 rounded-md border border-efs-border bg-efs-bg-subtle px-1.5 py-0.5 font-mono text-[10px] text-efs-text-muted hover:bg-efs-bg-panel"
                                title={t('efs.operations.inbound.photo_preview.button_tooltip', {
                                  defaultValue: '写真をプレビュー',
                                })}
                                aria-label={t(
                                  'efs.operations.inbound.photo_preview.button_tooltip',
                                  {
                                    defaultValue: '写真をプレビュー',
                                  },
                                )}
                              >
                                <Camera className="h-3 w-3" />
                                {pkg.photo_evidence_file_ids.length}
                              </button>
                            ) : null}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs">
                          {pkg.package_type_id
                            ? (packageTypeMap.get(pkg.package_type_id) ??
                              t('efs.operations.inbound.fields.value_unavailable', {
                                defaultValue: '名称を取得できません',
                              }))
                            : '—'}
                          <FieldEditedMark edited={edited.has('package_type_id')} />
                        </TableCell>
                        <TableCell className="text-xs">
                          {pkg.warehouse_id
                            ? (warehouseMap.get(pkg.warehouse_id) ??
                              t('efs.operations.inbound.fields.value_unavailable', {
                                defaultValue: '名称を取得できません',
                              }))
                            : '—'}
                          <FieldEditedMark edited={edited.has('warehouse_id')} />
                        </TableCell>
                        <TableCell className="text-right">
                          {pkg.gross_kg ? <KgValue value={pkg.gross_kg} /> : '—'}
                          <FieldEditedMark edited={edited.has('gross_kg')} />
                        </TableCell>
                        <TableCell className="text-right">
                          {pkg.tare_kg ? <KgValue value={pkg.tare_kg} /> : '—'}
                          <FieldEditedMark edited={edited.has('tare_kg')} />
                        </TableCell>
                        <TableCell className="text-right">
                          {pkg.net_kg ? <KgValue value={pkg.net_kg} /> : '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          {(() => {
                            // Per-package completion %: 4 required fields
                            // (package_type, gross, tare, ≥1 photo). Warehouse
                            // is optional so it doesn't count. A completed
                            // package always shows 100%.
                            const required = 4;
                            const done =
                              (pkg.package_type_id ? 1 : 0) +
                              (pkg.gross_kg ? 1 : 0) +
                              (pkg.tare_kg ? 1 : 0) +
                              ((pkg.photo_evidence_file_ids ?? []).length > 0 ? 1 : 0);
                            const pct = Math.round((done / required) * 100);
                            return (
                              <span
                                className={
                                  'font-mono text-xs ' +
                                  (isDeleted
                                    ? 'text-red-900 dark:text-red-100'
                                    : pct === 100
                                      ? 'text-emerald-900 dark:text-emerald-100'
                                      : 'text-amber-900 dark:text-amber-100')
                                }
                              >
                                {isDeleted
                                  ? t('efs.operations.inbound.lot_detail.status_deleted', {
                                      defaultValue: '削除済',
                                    })
                                  : `${pct}%`}
                              </span>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="text-right">
                          {(pkg.revisions ?? []).length > 0 ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setRevPkg(pkg);
                                setRevOpen(true);
                              }}
                              title={t('efs.operations.inbound.revisions.button_tooltip', {
                                defaultValue: '異動紀錄を表示',
                              })}
                            >
                              <ClipboardList className="mr-1 h-3 w-3" />
                              {pkg.revisions.length}
                            </Button>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex flex-col items-end gap-1">
                            <div className="flex justify-end gap-1">
                              {!isDeleted ? (
                                <>
                                  {lot.status === 'imported' ? (
                                    <>
                                      <Button
                                        size="sm"
                                        variant={pkg.status === 'pending' ? 'default' : 'outline'}
                                        onClick={() => openEdit(pkg)}
                                      >
                                        <Pencil className="mr-1 h-3 w-3" />
                                        {pkg.status === 'pending'
                                          ? t('efs.operations.inbound.lot_detail.edit_package')
                                          : t('efs.operations.inbound.lot_detail.edit_again', {
                                              defaultValue: '再編集',
                                            })}
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => openDelete(pkg)}
                                      >
                                        <Trash2 className="mr-1 h-3 w-3" />
                                        {t('efs.operations.inbound.lot_detail.delete_package', {
                                          defaultValue: '削除',
                                        })}
                                      </Button>
                                    </>
                                  ) : (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => openWeightCorrection(pkg)}
                                      disabled={!canRequestWeightCorrection}
                                    >
                                      <Scale className="mr-1 h-3 w-3" />
                                      {t('efs.operations.inbound.weight_correction.action')}
                                    </Button>
                                  )}
                                </>
                              ) : null}
                            </div>
                            {!isDeleted && lot.status !== 'imported' ? (
                              <p className="max-w-52 text-right text-xs text-amber-900 dark:text-amber-100">
                                {t(
                                  canRequestWeightCorrection
                                    ? 'efs.operations.inbound.weight_correction.locked_hint'
                                    : 'efs.operations.inbound.weight_correction.permission_hint',
                                )}
                              </p>
                            ) : null}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
                {receivedPackages.length > 0
                  ? (() => {
                      // Derived (re-dismantling) units are excluded — they are
                      // output of this lot, not part of what arrived.
                      const sumGross = receivedPackages.reduce(
                        (s, p) => s + (p.gross_kg ? Number(p.gross_kg) : 0),
                        0,
                      );
                      const sumTare = receivedPackages.reduce(
                        (s, p) => s + (p.tare_kg ? Number(p.tare_kg) : 0),
                        0,
                      );
                      const sumNet = receivedPackages.reduce(
                        (s, p) => s + (p.net_kg ? Number(p.net_kg) : 0),
                        0,
                      );
                      const fmt = (n: number) => n.toFixed(1);
                      return (
                        <TableRow className="bg-efs-bg-subtle font-semibold">
                          <TableCell colSpan={5} className="text-right text-xs">
                            {t('efs.operations.inbound.lot_detail.row_totals', {
                              defaultValue: '合計',
                            })}
                          </TableCell>
                          <TableCell className="text-right">{fmt(sumGross)} kg</TableCell>
                          <TableCell className="text-right">{fmt(sumTare)} kg</TableCell>
                          <TableCell className="text-right">{fmt(sumNet)} kg</TableCell>
                          <TableCell colSpan={3} />
                        </TableRow>
                      );
                    })()
                  : null}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {canonicalBatchId ? (
        <InboundPackageEditDialog
          batchId={canonicalBatchId}
          pkg={editPkg}
          open={editOpen}
          onOpenChange={(v) => {
            setEditOpen(v);
            if (!v) setEditPkg(null);
          }}
        />
      ) : null}

      {canonicalBatchId ? (
        <InboundPackageWeightCorrectionDialog
          batchId={canonicalBatchId}
          pkg={weightCorrectionPkg}
          open={weightCorrectionPkg != null}
          onOpenChange={(open) => {
            if (!open) setWeightCorrectionPkg(null);
          }}
          applyImmediately={canApplyWeightCorrection}
        />
      ) : null}

      <WorksheetEditDialog
        open={worksheetEditOpen}
        onOpenChange={setWorksheetEditOpen}
        lot={lot ?? undefined}
      />

      <DeleteInboundLotDialog
        open={lotDeleteOpen}
        onOpenChange={setLotDeleteOpen}
        lot={lot ?? null}
        showTechnicalDetails={canViewTechnicalErrors}
        onDeleted={() => {
          if (canonicalBatchId) {
            navigate(`/batches/${canonicalBatchId}/inbound`, { replace: true });
          }
        }}
      />

      <Dialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteTarget(null);
            setDeleteReason('');
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {t('efs.operations.inbound.package_delete.title', {
                defaultValue: '包装を削除',
              })}
            </DialogTitle>
            <DialogDescription>
              {deleteTarget
                ? t('efs.operations.inbound.package_delete.description', {
                    code: deleteTarget.package_code,
                    item: deleteTarget.raw_incoming_item_name,
                    defaultValue:
                      '{{code}} — {{item}} を削除済として記録します。理由は変更履歴に残ります。',
                  })
                : null}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="inbound-package-delete-reason">
              {t('efs.operations.inbound.package_delete.reason', {
                defaultValue: '削除理由',
              })}
              <span className="ml-1 text-red-900">*</span>
            </label>
            <Textarea
              id="inbound-package-delete-reason"
              rows={3}
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder={t('efs.operations.inbound.package_delete.reason_placeholder', {
                defaultValue: '例: 誤って登録したため',
              })}
            />
            {deletePackage.error ? (
              <LocalizedApiError
                error={deletePackage.error}
                showTechnicalDetails={canViewTechnicalErrors}
              />
            ) : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setDeleteTarget(null);
                setDeleteReason('');
              }}
              disabled={deletePackage.isPending}
            >
              {t('efs.common.actions.cancel', { ns: 'common' })}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => void submitDelete()}
              disabled={deletePackage.isPending || !deleteReason.trim()}
            >
              {deletePackage.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
              {t('efs.operations.inbound.package_delete.confirm', {
                defaultValue: '削除済にする',
              })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reason-prompt for bulk warehouse assignment when at least one
          completed package would change. */}
      <Dialog
        open={reasonModal.open}
        onOpenChange={(v) => setReasonModal((m) => ({ ...m, open: v }))}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t('efs.operations.inbound.bulk_assign.reason_title', {
                defaultValue: '修改原因（監査ログ）',
              })}
            </DialogTitle>
            <DialogDescription>
              {t('efs.operations.inbound.bulk_assign.reason_subtitle', {
                count: reasonModal.affectedCount,
                wh: reasonModal.warehouseLabel,
                defaultValue:
                  '完成済 {{count}} 件の保管倉庫を「{{wh}}」へ変更します。修改原因を入力してください（必須）。',
              })}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            rows={3}
            value={reasonText}
            onChange={(e) => setReasonText(e.target.value)}
            placeholder={t('efs.operations.inbound.package_edit.change_reason_placeholder', {
              defaultValue: '例: 倉庫レイアウト変更',
            })}
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setReasonModal((m) => ({ ...m, open: false }))}
              disabled={bulkAssign.isPending}
            >
              {t('efs.common.actions.cancel', { ns: 'common' })}
            </Button>
            <Button
              disabled={bulkAssign.isPending || !reasonText.trim()}
              onClick={async () => {
                await runBulkAssign(reasonModal.warehouseId, reasonText.trim());
                setReasonModal((m) => ({ ...m, open: false }));
              }}
            >
              {bulkAssign.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
              {t('efs.operations.inbound.bulk_assign.confirm', {
                defaultValue: '保存して反映',
              })}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!truckRemoveTargetFid}
        onOpenChange={(open) => {
          if (!open && !removeTruckPhotoMutation.isPending) {
            setTruckRemoveTargetFid(null);
            setTruckRemoveReason('');
            setTruckError(null);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t('efs.operations.inbound.truck_photos.remove_confirm_title', {
                defaultValue: 'トラック写真をこの入荷から外しますか？',
              })}
            </DialogTitle>
            <DialogDescription>
              {t('efs.operations.inbound.truck_photos.remove_confirm_description', {
                defaultValue:
                  '証跡ファイルとファイル庫の元データは削除せず、この入荷ロットとの関連だけを解除します。',
              })}
            </DialogDescription>
          </DialogHeader>
          {truckError ? (
            <p role="alert" className="text-sm text-red-900 dark:text-red-100">
              {truckError}
            </p>
          ) : null}
          {lot?.status === 'confirmed' ? (
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="truck-photo-remove-reason">
                {t('efs.operations.inbound.truck_photos.remove_reason_label', {
                  defaultValue: '変更理由',
                })}
              </label>
              <Textarea
                id="truck-photo-remove-reason"
                rows={3}
                value={truckRemoveReason}
                onChange={(event) => {
                  setTruckRemoveReason(event.target.value);
                  setTruckError(null);
                }}
                placeholder={t('efs.operations.inbound.truck_photos.remove_reason_placeholder', {
                  defaultValue: '例: 同じファイルが左右に重複登録されていたため',
                })}
              />
            </div>
          ) : null}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setTruckRemoveTargetFid(null);
                setTruckRemoveReason('');
                setTruckError(null);
              }}
              disabled={removeTruckPhotoMutation.isPending}
            >
              {t('efs.common.actions.cancel', { ns: 'common' })}
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleTruckPhotoRemove()}
              disabled={
                removeTruckPhotoMutation.isPending ||
                (lot?.status === 'confirmed' && truckRemoveReason.trim().length === 0)
              }
            >
              {removeTruckPhotoMutation.isPending ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : null}
              {t('efs.operations.inbound.truck_photos.remove_confirm_action', {
                defaultValue: 'この入荷から外す',
              })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generic photo preview modal (used by truck panorama + per-package). */}
      <Dialog
        open={!!photoPreview}
        onOpenChange={(v) => {
          if (!v) setPhotoPreview(null);
        }}
      >
        <DialogContent className="max-h-[85vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{photoPreview?.title ?? ''}</DialogTitle>
            {photoPreview?.subtitle ? (
              <DialogDescription>
                <span className="font-mono text-xs">{photoPreview.subtitle}</span>
              </DialogDescription>
            ) : null}
          </DialogHeader>
          {photoPreview ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {photoPreview.fids.map((fid, index) => {
                const photoLabel = t('efs.operations.inbound.truck_photos.preview_photo_label', {
                  index: index + 1,
                  defaultValue: '写真 {{index}}',
                });
                return (
                  <div
                    key={fid}
                    className="overflow-hidden rounded-md border border-efs-border bg-efs-bg-subtle"
                  >
                    {/* Photos render inline; videos fall back to a <video>
                      player. Backend serves both with `Content-Disposition:
                      inline` so they preview directly in-tab too. */}
                    <EvidenceMedia fid={fid} alt={photoLabel} />
                    <div className="px-2 py-1 text-[10px] text-efs-text-muted">
                      <a
                        href={evidenceDownloadUrl(fid)}
                        target="_blank"
                        rel="noreferrer"
                        className="underline"
                        aria-label={t('efs.operations.inbound.truck_photos.open_original_photo', {
                          label: photoLabel,
                          defaultValue: '{{label}}の原本を開く',
                        })}
                      >
                        {t('efs.operations.inbound.truck_photos.open_original_photo', {
                          label: photoLabel,
                          defaultValue: '{{label}}の原本を開く',
                        })}
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <LotRevisionsModal
        open={worksheetRevOpen}
        onOpenChange={setWorksheetRevOpen}
        lotCode={lot?.lot_code}
        revisions={lot?.revisions ?? []}
        fieldLabel={fieldLabel}
        valueLabel={revisionValueLabel}
      />

      <RevisionsModal
        open={revOpen}
        onOpenChange={(v) => {
          setRevOpen(v);
          if (!v) setRevPkg(null);
        }}
        pkg={revPkg}
        fieldLabel={fieldLabel}
        valueLabel={revisionValueLabel}
      />

      {/* Floating bottom action bar for bulk warehouse assignment.
          Uses fixed positioning so it overlays the page without pushing
          the packages table down when checkboxes are toggled. */}
      {lot && lot.status === 'imported' && selectedPkgIds.size > 0 ? (
        <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-4 pb-4">
          <div className="pointer-events-auto flex flex-wrap items-center gap-2 rounded-full border border-amber-300 bg-amber-50/95 px-4 py-2 text-sm shadow-lg backdrop-blur dark:border-amber-700 dark:bg-amber-950">
            <span className="font-semibold text-amber-900 dark:text-amber-100">
              {t('efs.operations.inbound.bulk_assign.selected', {
                count: selectedPkgIds.size,
                defaultValue: '選択中 {{count}} 件 → 保管倉庫:',
              })}
            </span>
            {warehouses.map((wh) => (
              <Button
                key={wh.warehouse_id}
                size="sm"
                variant="outline"
                onClick={() =>
                  void handleBulkAssign(wh.warehouse_id, `${wh.code} — ${wh.display_name}`)
                }
                disabled={bulkAssign.isPending}
              >
                {wh.code} — {wh.display_name}
              </Button>
            ))}
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                void handleBulkAssign(
                  null,
                  t('efs.operations.inbound.bulk_assign.clear', { defaultValue: '解除' }),
                )
              }
              disabled={bulkAssign.isPending}
            >
              {t('efs.operations.inbound.bulk_assign.clear', { defaultValue: '解除' })}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSelectedPkgIds(new Set())}
              disabled={bulkAssign.isPending}
            >
              {t('efs.operations.inbound.bulk_assign.cancel', { defaultValue: '選択解除' })}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default InboundLotDetailPage;
